import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, Edit3, Trash2, Eye, Copy, Award, ToggleLeft, ToggleRight,
  FileText, ChevronLeft, ChevronRight, X, Send
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import IconButton from '../components/IconButton';

const formatDate = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const inputClass = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm";

const CertModelos = () => {
  const navigate = useNavigate();
  const [modelos, setModelos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingModelo, setEditingModelo] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formData, setFormData] = useState({
    nome_modelo: '',
    desc_modelo: '',
    orientacao: 'paisagem',
    largura_m: 297,
    altura_m: 210,
    ativo: 1
  });

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 10;

  useEffect(() => { loadModelos(); }, [page]);
  useEffect(() => { document.title = 'Modelos de Certificado - EstOX'; }, []);

  const loadModelos = async () => {
    setLoading(true);
    try {
      const params = { page, limit: pageSize };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      const response = await api.get('/cert-modelos', { params });
      setModelos(response.data.data || []);
      setTotal(response.data.pagination?.total || 0);
      setTotalPages(response.data.pagination?.totalPages || 1);
    } catch (error) {
      console.error('Erro ao carregar modelos:', error);
      toast.error('Erro ao carregar modelos');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => { setPage(1); loadModelos(); };

  const openModal = (modelo = null) => {
    setEditingModelo(modelo);
    setFormData({
      nome_modelo: modelo?.nome_modelo || '',
      desc_modelo: modelo?.desc_modelo || '',
      orientacao: modelo?.orientacao || 'paisagem',
      largura_m: modelo?.largura_m || 297,
      altura_m: modelo?.altura_m || 210,
      ativo: modelo?.ativo ?? 1
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingModelo(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.nome_modelo.trim()) return toast.error('Nome do modelo é obrigatório');

    setIsSaving(true);
    try {
      if (editingModelo) {
        await api.put(`/cert-modelos/${editingModelo.id_modelo}`, formData);
        toast.success('Modelo atualizado com sucesso!');
      } else {
        await api.post('/cert-modelos', formData);
        toast.success('Modelo criado com sucesso!');
      }
      closeModal();
      loadModelos();
    } catch (error) {
      console.error('Erro ao salvar modelo:', error);
      toast.error(error?.response?.data?.message || 'Erro ao salvar modelo');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await api.post(`/cert-modelos/${id}/duplicar`);
      toast.success('Modelo duplicado com sucesso!');
      loadModelos();
    } catch (error) {
      toast.error('Erro ao duplicar modelo');
    }
  };

  const handleToggleAtivo = async (modelo) => {
    try {
      await api.patch(`/cert-modelos/${modelo.id_modelo}/ativo`);
      toast.success(`Modelo ${modelo.ativo ? 'inativado' : 'ativado'} com sucesso!`);
      loadModelos();
    } catch (error) {
      toast.error('Erro ao alterar situação');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/cert-modelos/${deleteTarget.id_modelo}`);
      toast.success('Modelo excluído com sucesso!');
      setDeleteTarget(null);
      loadModelos();
    } catch (error) {
      toast.error('Erro ao excluir modelo');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">Modelo de Certificado</h1>
          <p className="text-gray-600 dark:text-gray-400">Gerencie modelos e gere certificados para os participantes dos cursos</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/emitir-certificados')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Send className="w-4 h-4" /> Emitir Certificados
          </button>
          <IconButton icon={Plus} onClick={() => openModal()} variant="primary" title="Novo Modelo" />
        </div>
      </div>

      {/* Cards de resumo */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <Award className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Total de modelos</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">{total}</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
            <ToggleRight className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Ativos</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">{modelos.filter(m => m.ativo).length}</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
            <FileText className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Inativos</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">{modelos.filter(m => !m.ativo).length}</p>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="card p-4 mb-6">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nome ou descrição..."
              className={`${inputClass} pl-10`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <IconButton icon={Search} onClick={handleSearch} variant="primary" title="Buscar" />
        </div>
      </div>

      {/* Tabela */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nome</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Descrição</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tamanho</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Criação</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Situação</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr><td colSpan={7} className="px-6 py-8 text-center text-gray-500">Carregando...</td></tr>
              ) : modelos.length === 0 ? (
                <tr><td colSpan={7} className="px-6 py-8 text-center text-gray-500">Nenhum modelo encontrado</td></tr>
              ) : modelos.map((m) => (
                <tr key={m.id_modelo} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">#{m.id_modelo}</td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{m.nome_modelo}</td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400 max-w-xs truncate">{m.desc_modelo || '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                    {m.largura_m} × {m.altura_m} mm
                    <span className="block text-xs text-gray-400">{m.orientacao === 'paisagem' ? 'Paisagem' : 'Retrato'}</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{formatDate(m.dt_criacao)}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                      m.ativo ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {m.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-1">
                      <IconButton icon={Eye} onClick={() => navigate(`/cert-modelos/${m.id_modelo}/editar`)} variant="info" size="sm" title="Editar layout" />
                      <IconButton icon={Edit3} onClick={() => openModal(m)} variant="warning" size="sm" title="Editar dados" />
                      <IconButton icon={Copy} onClick={() => handleDuplicate(m.id_modelo)} variant="primary" size="sm" title="Duplicar" />
                      <IconButton
                        icon={m.ativo ? ToggleRight : ToggleLeft}
                        onClick={() => handleToggleAtivo(m)}
                        variant={m.ativo ? 'success' : 'secondary'}
                        size="sm"
                        title={m.ativo ? 'Inativar' : 'Ativar'}
                      />
                      <IconButton icon={Trash2} onClick={() => setDeleteTarget(m)} variant="danger" size="sm" title="Excluir" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 dark:border-gray-700">
            <span className="text-sm text-gray-500">Página {page} de {totalPages}</span>
            <div className="flex gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="p-1.5 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                className="p-1.5 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de cadastro/edição */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingModelo ? 'Editar Modelo' : 'Novo Modelo'}
              </h2>
              <IconButton icon={X} onClick={closeModal} variant="secondary" size="sm" />
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome do modelo *</label>
                <input className={inputClass} value={formData.nome_modelo}
                  onChange={(e) => setFormData({ ...formData, nome_modelo: e.target.value })} placeholder="Ex.: Certificado Padrão 2025" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Descrição</label>
                <textarea className={inputClass} value={formData.desc_modelo}
                  onChange={(e) => setFormData({ ...formData, desc_modelo: e.target.value })} placeholder="Descrição opcional..." rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Orientação</label>
                  <select className={inputClass} value={formData.orientacao}
                    onChange={(e) => setFormData({ ...formData, orientacao: e.target.value })}>
                    <option value="paisagem">Paisagem (horizontal)</option>
                    <option value="retrato">Retrato (vertical)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Situação</label>
                  <select className={inputClass} value={formData.ativo}
                    onChange={(e) => setFormData({ ...formData, ativo: Number(e.target.value) })}>
                    <option value={1}>Ativo</option>
                    <option value={0}>Inativo</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Largura (mm)</label>
                  <input type="number" className={inputClass} value={formData.largura_m}
                    onChange={(e) => setFormData({ ...formData, largura_m: Number(e.target.value) })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Altura (mm)</label>
                  <input type="number" className={inputClass} value={formData.altura_m}
                    onChange={(e) => setFormData({ ...formData, altura_m: Number(e.target.value) })} />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button type="button" onClick={closeModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600">
                  Cancelar
                </button>
                <button type="submit" disabled={isSaving}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {isSaving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de confirmação de exclusão */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Confirmar exclusão</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Tem certeza que deseja excluir o modelo "{deleteTarget.nome_modelo}"? Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600">
                Cancelar
              </button>
              <button onClick={confirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertModelos;
